import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    Signal,
    signal,
    viewChild,
    WritableSignal,
} from "@angular/core";
import { QuestSchedulesStore } from "../../../../stores/quests/quest-schedule.store";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from "@angular/material/menu";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { ComponentRoutes } from "../../../../models/constants/component-routes";
import { QuestScheduleViewModel } from "@playcharge/common/models/backend/quests/backoffice/quest-schedule-view-model";
import { QuestRepeatType } from "@playcharge/common/models/backend/quests/quest-repeat-type";
import { Nullable } from "@playcharge/common/models/backend/nullable";
import { MatDialog } from "@angular/material/dialog";
import { QuestScheduleCreateDialogComponent } from "../quest-schedule-create-dialog/quest-schedule-create-dialog.component";
import { QuestScheduleEditDialogComponent } from "../quest-schedule-edit-dialog/quest-schedule-edit-dialog.component";
import { EMPTY, mergeMap } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { QuestScheduleCreateModel } from "@playcharge/common/models/backend/quests/backoffice/quest-schedule-create-model";

@Component({
    selector: "app-quests-schedules",
    standalone: true,
    imports: [
        CommonModule,
        MatTableModule,
        MatSortModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatIconModule,
        MatMenuModule,
        MatButtonModule,
    ],
    templateUrl: "./quest-schedules.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestSchedulesComponent {
    private _matDialog: MatDialog = inject(MatDialog);
    private _toastrService: ToastrService = inject(ToastrService);

    public schedulesStore: QuestSchedulesStore = inject(QuestSchedulesStore);

    public displayedColumns: WritableSignal<string[]> = signal<string[]>([
        "name",
        "repeat",
        "retentionDays",
        "actions",
    ]);

    public dataSource: MatTableDataSource<QuestScheduleViewModel> =
        new MatTableDataSource<QuestScheduleViewModel>();

    private _matSort: Signal<Nullable<MatSort>> = viewChild<Nullable<MatSort>>(MatSort);

    public ComponentRoutes: typeof ComponentRoutes = ComponentRoutes;
    public QuestRepeatType: typeof QuestRepeatType = QuestRepeatType;

    constructor() {
        effect(() => {
            if (this._matSort()) {
                this.dataSource.sort = this._matSort()!;
            }
        });

        effect(() => {
            this.dataSource.data = this.schedulesStore.entities();
        });
    }

    public applyFilter(event: KeyboardEvent): void {
        this.dataSource.filter = (event.target as HTMLInputElement).value;
    }

    public addSchedule(): void {
        this._matDialog
            .open<QuestScheduleCreateDialogComponent, any, QuestScheduleCreateModel>(
                QuestScheduleCreateDialogComponent,
            )
            .afterClosed()
            .pipe(
                mergeMap((value) => {
                    if (value) {
                        return this.schedulesStore.createSchedule(value);
                    }
                    return EMPTY;
                }),
            )
            .subscribe(() => {
                this._toastrService.success("Successfully created");
            });
    }

    public editSchedule(schedule: QuestScheduleViewModel): void {
        this._matDialog
            .open<QuestScheduleEditDialogComponent, QuestScheduleViewModel, { name: string }>(
                QuestScheduleEditDialogComponent,
                {
                    data: schedule,
                },
            )
            .afterClosed()
            .pipe(
                mergeMap((value) => {
                    if (value) {
                        return this.schedulesStore.editSchedule({
                            id: schedule.id,
                            name: value.name,
                        });
                    }

                    return EMPTY;
                }),
            )
            .subscribe(() => {
                this._toastrService.success("Successfully updated");
            });
    }
}
